import { useEffect, useState } from 'react';
import { settingsStore } from '@/game/settings';
import { cx } from '@/lib/cx';
import { qa, useQa } from '@/qa/recorder';
import { useElapsed } from '@/qa/useElapsed';
import s from './debug.module.css';
import { Card } from './ui';
import { download, fmtTime } from './util';

/**
 * QA test sessions (docs/design/qa-recording.md), the same recorder as in the grown-ups' settings:
 * start and stop a recording, and upload, download or delete saved sessions, with their details.
 */
export function QaSessionsPanel() {
  const state = useQa();
  const elapsed = useElapsed(state.startedAt);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void qa.recover();
  }, []);

  const run = (action: () => Promise<unknown>) => {
    setBusy(true);
    void action().finally(() => {
      setBusy(false);
    });
  };

  return (
    <>
      <Card
        title="Recording"
        actions={
          state.recording ? (
            <span className={cx(s.badge, s.badgeDanger)}>● recording {elapsed}</span>
          ) : (
            <span className={s.badge}>idle</span>
          )
        }
      >
        {state.recording ? (
          <>
            <label htmlFor="qa-note" className={s.hint}>
              Note for this session (what you tested, what went wrong)
            </label>
            <textarea
              id="qa-note"
              className={s.textarea}
              style={{ minHeight: 60, fontFamily: 'inherit' }}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
            <div className={s.row}>
              <button
                type="button"
                className={cx(s.btn, s.dangerSolid)}
                disabled={busy}
                onClick={() => {
                  run(async () => {
                    await qa.stop(note.trim() || undefined);
                    setNote('');
                  });
                }}
              >
                Stop and save
              </button>
            </div>
          </>
        ) : (
          <>
            <p className={s.hint}>
              Records the microphone, what the app says and its decisions while you play. Start
              here, then go to a screen.
            </p>
            <div className={s.row}>
              <button
                type="button"
                className={cx(s.btn, s.primary)}
                disabled={busy}
                onClick={() => {
                  run(() =>
                    qa.start({
                      age: settingsStore.get().age,
                      route: window.location.hash || '#/',
                    }),
                  );
                }}
              >
                Start recording
              </button>
            </div>
          </>
        )}
        {state.status && <p className={cx(s.hint, s.mono)}>{state.status}</p>}
      </Card>

      <Card title={`Saved sessions (${String(state.pending.length)})`} flush>
        {state.pending.length === 0 ? (
          <p className={s.empty}>No sessions waiting on this device.</p>
        ) : (
          <div className={s.tableWrap}>
            <table className={cx(s.table, s.stack)}>
              <thead>
                <tr>
                  <th>Session</th>
                  <th className={s.hideNarrow}>Started</th>
                  <th className={s.num}>Length</th>
                  <th className={s.hideNarrow}>Note</th>
                  <th>
                    <span className={s.visuallyHidden}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.pending.map(({ manifest: m, state: st }) => (
                  <tr key={m.id}>
                    <td>
                      <span className={s.mono}>{m.id}</span>{' '}
                      <span className={cx(s.badge, st === 'stopped' && s.badgeWarn)}>{st}</span>
                    </td>
                    <td data-label="Started" className={s.hideNarrow}>
                      {fmtTime(Date.parse(m.startedAt))}
                    </td>
                    <td data-label="Length" className={cx(s.num, s.mono)}>
                      {m.durationMs ? `${String(Math.round(m.durationMs / 1000))} s` : '–'}
                    </td>
                    <td data-label="Note" className={cx(s.hideNarrow, s.truncate)} title={m.note}>
                      {m.note ?? ''}
                    </td>
                    <td>
                      <div
                        className={s.row}
                        style={{ flexWrap: 'nowrap', justifyContent: 'flex-end' }}
                      >
                        <button
                          type="button"
                          className={cx(s.btn, s.small)}
                          disabled={busy}
                          onClick={() => {
                            run(() => qa.upload(m.id));
                          }}
                        >
                          Upload
                        </button>
                        <button
                          type="button"
                          className={cx(s.btn, s.small)}
                          disabled={busy}
                          onClick={() => {
                            run(async () => {
                              download(`foe-names-qa-${m.id}.zip`, await qa.zip(m.id));
                            });
                          }}
                        >
                          Zip
                        </button>
                        <button
                          type="button"
                          className={cx(s.btn, s.small, s.danger)}
                          disabled={busy}
                          onClick={() => {
                            run(() => qa.remove(m.id));
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
